import { useState } from 'react';
import { CATALOG, SHEET_LABELS, TENT_LAYOUT_LABELS, applyProductChange, applyTemplate, calendarPages, dlTentProduct, impositionPlan, tentLayout, tentPieceSize, MIN_YEAR, MAX_YEAR, type ProjectDoc } from '@cal/engine';
import { Check, Field, NumberField, Select } from '../ui/controls';
import { edit } from './store';

export function ImpositionNote({ doc }: { doc: ProjectDoc }) {
  const p = doc.product;
  if (p.bleedMm <= 0) return <p className="small faint">No bleed: the file prints at actual size and is not cut or imposed.</p>;
  const plan = impositionPlan(doc);
  const imp = plan.imp;
  if (!imp) return <p className="small faint">With bleed this size is larger than SRA3, so only single-up files are produced (large format).</p>;
  const how = {
    repeat: p.productType === 'tent' ? ' — each sheet repeats the flat tent card.' : ' — each sheet repeats one side.',
    ganged: ' — consecutive pages ganged on each sheet.',
    duplex: ' — front sheets followed by their backs: columns mirrored for a left-to-right sheet turn, backs head-to-foot (tumble).',
  }[plan.mode];
  const c = plan.carrier;
  return (
    <p className="small faint">
      Imposed output: <strong>{imp.perSheet}-up</strong> on SRA3 ({imp.cols} × {imp.rows}, {imp.sheetW > imp.sheetH ? 'landscape' : 'portrait'} sheet), {imp.pieceW} × {imp.pieceH} mm pieces butted together and centred, no crop marks{how}
      {c && (c.imp ? ` Carrier cards follow on their own sheet, ${c.imp.perSheet}-up.` : ' The carrier card is too large for SRA3 and is supplied single-up only.')}
    </p>
  );
}

export function ProductStage({ doc }: { doc: ProjectDoc }) {
  const p = doc.product;
  const isYear = p.productType === 'year';
  const isTent = p.productType === 'tent';
  const isMonthly = p.productType === 'monthly';
  const withBleed = isTent ? tentPieceSize(p) : { w: p.widthMm + 2 * p.bleedMm, h: p.heightMm + 2 * p.bleedMm };
  const describe = (t: string) => (t === 'year' ? 'single year-at-a-glance page' : t === 'tent' ? 'folded tent card (two faces of six months)' : '12-month calendar');
  const [unlocked, setUnlocked] = useState(false);
  const catalogItem = CATALOG.find((c) => c.id === p.catalogId);
  const locked = !!catalogItem && !unlocked;
  const change = (patch: Partial<ProjectDoc['product']>, key = Object.keys(patch).join(',')) => edit((d) => applyProductChange(d, patch), `product:${key}`);
  return (
    <div>
      <div className="section col">
        <h2>1 · Product</h2>
        <Select label="Product" value={p.catalogId ?? ''} onChange={(id) => {
          const c = CATALOG.find((x) => x.id === id);
          if (!c) return;
          if (c.product.productType !== p.productType && !confirm(`Switch to “${c.name}”? The layout changes to a ${describe(c.product.productType!)}. Photos, holidays and branding are kept; you can undo.`)) return;
          edit((d) => {
            let out = applyProductChange(d, { ...c.product, catalogId: c.id });
            if (c.product.productType !== d.product.productType) out = applyTemplate(out, c.templateId);
            return { ...out, product: { ...out.product, ...c.product, catalogId: c.id } };
          }, 'catalog');
        }} options={[{ value: '', label: isYear ? 'Year planner (custom)' : isTent ? 'Tent card (custom)' : 'Monthly calendar (custom)' }, ...CATALOG.map((c) => ({ value: c.id, label: c.name, group: c.group }))]}
          hint={isYear ? 'One page showing all 12 months (plus an optional reverse side).' : isTent ? TENT_LAYOUT_LABELS[p.tent.layout] : p.carrier.enabled ? '12 tear-off leaves plus a carrier card.' : p.duplex ? 'Double-sided leaves: Jan–Jun on the fronts, Jul–Dec on the backs.' : '12 month pages, or 13 with a cover.'} />
        <NumberField label="Calendar year" value={doc.year} min={MIN_YEAR} max={MAX_YEAR} step={1} digits={0}
          onChange={(y) => edit((d) => ({ ...d, year: Math.round(y) }), 'year')}
          hint={doc.eventSets.some((s) => s.year !== null && s.year !== doc.year) ? <span style={{ color: 'var(--err)' }}>Some holiday data is for another year — update it in Content.</span> : undefined} />
        {locked && (
          <div className="notice info small row wrap" style={{ gap: 8 }}>
            <span className="grow">Size, bleed and safe area are set for “{catalogItem.name}”.</span>
            <button className="btn sm" onClick={() => { if (confirm('Change the size, bleed or safe area of this catalogue product? The printed result may no longer match the standard product.')) setUnlocked(true); }}>Unlock to customise</button>
          </div>
        )}
        <Select label="Finished size" disabled={locked} value={p.sheetPreset} onChange={(v) => change({ sheetPreset: v })}
          options={[...Object.entries(SHEET_LABELS).map(([value, label]) => ({ value: value as ProjectDoc['product']['sheetPreset'], label })), { value: 'custom', label: 'Custom size' }]} />
        <Select label="Orientation" value={p.orientation} disabled={locked || p.sheetPreset === 'custom'} onChange={(v) => change({ orientation: v })}
          options={[{ value: 'portrait', label: 'Portrait' }, { value: 'landscape', label: 'Landscape' }]} />
        {p.sheetPreset === 'custom' && (
          <div className="grid2">
            <NumberField label="Width" unit="mm" disabled={locked} value={p.widthMm} min={50} max={2000} onChange={(v) => change({ widthMm: v }, 'custom')} />
            <NumberField label="Height" unit="mm" disabled={locked} value={p.heightMm} min={50} max={2000} onChange={(v) => change({ heightMm: v }, 'custom')} />
          </div>
        )}
        <p className="small faint">{isTent ? 'Flat sheet' : p.carrier.enabled ? 'Tear-off leaf' : 'Finished (trimmed) size'} {p.widthMm} × {p.heightMm} mm{p.bleedMm > 0 ? `; with bleed ${withBleed.w} × ${withBleed.h} mm${isTent && p.tent.layout !== 'classic' ? ' (no bleed on the tape strip edge)' : ''}` : ''}. Changing size rescales the design.</p>
      </div>
      <div className="section col">
        <h3>Edges & imposition</h3>
        <Select label="Edges" disabled={locked} value={p.bleedMm > 0 ? 'bleed' : 'none'} onChange={(v) => change(v === 'bleed' ? { bleedMm: 3 } : { bleedMm: 0, safeMarginMm: Math.max(5, p.safeMarginMm), cropMarks: false }, 'edges')}
          options={[{ value: 'bleed', label: 'With bleed — trimmed after printing' }, { value: 'none', label: 'No bleed — print actual size (A4/A3, no cutting)' }]}
          hint={p.bleedMm > 0 ? 'Use when photos or colour run to the page edge.' : 'Keep text and logos 5 mm from the edge; the printer cannot print to the very edge.'} />
        <div className="grid2">
          {p.bleedMm > 0 && <NumberField label="Bleed" unit="mm" disabled={locked} value={p.bleedMm} min={0.5} max={20} step={0.5} onChange={(v) => change({ bleedMm: v })} />}
          <NumberField label="Safe margin" unit="mm" disabled={locked} value={p.safeMarginMm} min={0} max={50} step={0.5} onChange={(v) => change({ safeMarginMm: v })} />
        </div>
        {p.bleedMm > 0 && <Check label="Crop marks on single-up production PDFs" checked={p.cropMarks} onChange={(v) => change({ cropMarks: v })} />}
        <ImpositionNote doc={doc} />
      </div>
      <div className="section col">
        <h3>Pages & extra artwork</h3>
        {isTent && <TentSettings doc={doc} change={change} />}
        {isMonthly && <Check label="Double-sided leaves (desk tent: Jan–Jun on the fronts, Jul–Dec on the backs)" checked={p.duplex} disabled={p.carrier.enabled} onChange={(v) => change({ duplex: v })} />}
        {isMonthly && <Check label={p.duplex ? 'Add a cover and back cover (7 leaves, 14 sides)' : 'Add a cover page (13 sheets: cover + 12 months)'} checked={p.includeCover} onChange={(v) => change({ includeCover: v })} />}
        {isMonthly && <CarrierSettings doc={doc} change={change} />}
        {!isTent && !p.carrier.enabled && <Check label={isYear ? 'Reverse side' : 'Backing board'} checked={p.backingBoard.enabled} onChange={(v) => change({ backingBoard: { ...p.backingBoard, enabled: v } })} />}
        {!isTent && !p.carrier.enabled && p.backingBoard.enabled && (
          <div className="col" style={{ paddingLeft: 24 }}>
            <Check label={isYear ? 'Print the reverse side (separate artwork page)' : 'Printed backing board (separate artwork file)'} checked={p.backingBoard.printed} onChange={(v) => change({ backingBoard: { ...p.backingBoard, printed: v } })} />
            <Field label="Note"><input className="input" value={p.backingBoard.note} placeholder={isYear ? 'e.g. magnet backing, blank' : 'e.g. 1000 gsm grey board'} onChange={(e) => change({ backingBoard: { ...p.backingBoard, note: e.target.value } }, 'bbnote')} /></Field>
          </div>
        )}
        <p className="small faint">{pagesSummary(doc)}</p>
      </div>
      {isMonthly && (
        <div className="section col">
          <h3>Calendar area</h3>
          <Select label="Where the calendar grid sits" value={p.calendarArea} onChange={(v) => change({ calendarArea: v })}
            options={[{ value: 'bottom', label: 'Bottom half (photo above)' }, { value: 'top', label: 'Top half (photo below)' }, { value: 'full', label: 'Full page (no photo)' }, { value: 'custom', label: 'Custom (set the grid position in Design)' }]}
            hint="Choosing an area repositions the template elements; styles are kept." />
        </div>
      )}
      <div className="section col">
        <h3>Finishing</h3>
        <div className="grid2">
          <Select label="Clearance edge" value={p.bindingEdge} onChange={(v) => change({ bindingEdge: v, bindingClearanceMm: v === 'none' ? 0 : p.bindingClearanceMm || (v === 'top-bottom' ? 15 : 12) })}
            options={[{ value: 'top', label: 'Top (wire-o / hanger)' }, { value: 'left', label: 'Left' }, { value: 'top-bottom', label: 'Top and bottom (metal / tin rim)' }, { value: 'none', label: 'None' }]} />
          <NumberField label="Clearance" unit="mm" value={p.bindingClearanceMm} min={0} max={60} step={0.5} disabled={p.bindingEdge === 'none'} onChange={(v) => change({ bindingClearanceMm: v })} />
        </div>
        <Field label="Finishing notes"><textarea className="input" rows={2} value={p.finishing} onChange={(e) => change({ finishing: e.target.value }, 'finishing')} /></Field>
        <div className="notice info small">Clearance values are configurable defaults. The real clearance depends on the binding or rim equipment — confirm with production.</div>
      </div>
    </div>
  );
}

function pagesSummary(doc: ProjectDoc): string {
  const p = doc.product;
  if (p.productType === 'tent')
    return p.tent.layout === 'classic'
      ? 'One flat sheet, printed one side: both faces, base and glue tab.'
      : `One flat sheet, printed one side: ${calendarPages(doc).length} panels and a blank 15 mm tape strip. No crop or fold marks.`;
  if (p.productType === 'year') return `1 printed side${p.backingBoard.enabled ? (p.backingBoard.printed ? ' + printed reverse side' : ' + blank reverse') : ''}.`;
  const n = calendarPages(doc).length;
  if (p.carrier.enabled) return `${n} tear-off leaves + 1 carrier card.`;
  const sheets = p.duplex ? `${Math.ceil(n / 2)} double-sided leaves (${n} sides)` : `${n} printed calendar sheets`;
  return `${sheets}${p.backingBoard.enabled ? (p.backingBoard.printed ? ' + printed backing board' : ' + blank backing board') : ''}.`;
}

type Change = (patch: Partial<ProjectDoc['product']>, key?: string) => void;

/** Folded tent card: flat sheet = face B (upside down) | face A | base | glue tab. */
function TentSettings({ doc, change }: { doc: ProjectDoc; change: Change }) {
  const p = doc.product;
  const t = tentLayout(doc);
  const classic = p.tent.layout === 'classic';
  const scale = 140 / Math.max(t.w, t.h);
  const NAMES: Record<string, string> = {
    faceB: p.tent.layout === 'dl-landscape' || classic ? 'Back face · Jul–Dec (upside down)' : 'Panel 2 · Jul–Dec',
    faceA: p.tent.layout === 'dl-landscape' || classic ? 'Front face · Jan–Jun' : 'Panel 1 · Jan–Jun',
    info: p.tent.layout === 'dl-landscape' ? 'Base · other information' : 'Panel 3 · side information',
    tri1: 'Panel 1 · Jan–Apr', tri2: 'Panel 2 · May–Aug', tri3: 'Panel 3 · Sep–Dec',
    base: 'Base (blank)', tab: classic ? 'Glue tab (blank)' : 'Tape strip (blank)',
  };
  const SHORT: Record<string, string> = { faceA: 'A', faceB: 'B', info: 'info', tri1: '1', tri2: '2', tri3: '3', base: 'base', tab: 'tape' };
  const setLayout = (layout: ProjectDoc['product']['tent']['layout']) => {
    if (layout === p.tent.layout) return;
    if (!confirm('Change the tent style? The flat size and panels change; the panel designs are rebuilt (photos, holidays and branding are kept). You can undo.')) return;
    edit((d) => {
      const preset = layout === 'classic'
        ? { ...CATALOG.find((c) => c.id === 'tent-card')!.product, tent: { baseMm: 42, tabMm: 15, layout }, safeMarginMm: 5 }
        : dlTentProduct(layout);
      return applyTemplate(applyProductChange(d, preset), d.templateId);
    }, 'tent-layout');
  };
  return (
    <div className="col">
      <Select label="Tent style" value={p.tent.layout} onChange={setLayout}
        options={(Object.keys(TENT_LAYOUT_LABELS) as (keyof typeof TENT_LAYOUT_LABELS)[]).map((k) => ({ value: k, label: TENT_LAYOUT_LABELS[k] }))} />
      {classic && (
        <div className="grid2">
          <NumberField label="Base depth" unit="mm" value={p.tent.baseMm} min={0} max={300} step={1} onChange={(v) => change({ tent: { ...p.tent, baseMm: v } }, 'tent')} />
          <NumberField label="Glue tab" unit="mm" value={p.tent.tabMm} min={0} max={100} step={1} onChange={(v) => change({ tent: { ...p.tent, tabMm: v } }, 'tent')} />
        </div>
      )}
      <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
        <svg width={t.w * scale} height={t.h * scale} viewBox={`0 0 ${t.w} ${t.h}`} role="img" aria-label="Flat tent layout" style={{ flex: 'none', background: '#fff', border: '1px solid var(--line, #ccc)' }}>
          {t.panels.map((x) => (
            <g key={x.key}>
              <rect x={x.x} y={x.y} width={x.w} height={x.h} fill={x.printed ? '#e8eef6' : '#f6f6f6'} />
              <text x={x.x + x.w / 2} y={x.y + x.h / 2} fontSize={Math.min(14, x.h * 0.4, x.w * 0.4)} textAnchor="middle" dominantBaseline="middle" fill="#334" transform={x.rotate ? `rotate(180 ${x.x + x.w / 2} ${x.y + x.h / 2})` : undefined}>{SHORT[x.key]}</text>
            </g>
          ))}
          {t.folds.map((f, i) => <line key={i} x1={f.x1} x2={f.x2} y1={f.y1} y2={f.y2} stroke="#888" strokeWidth={1} strokeDasharray="4 3" />)}
        </svg>
        <ul className="small faint" style={{ margin: 0, paddingLeft: 16 }}>
          {t.panels.map((x) => <li key={x.key}>{NAMES[x.key]}: {+x.w.toFixed(1)} × {+x.h.toFixed(1)} mm</li>)}
          <li>Folds at {t.folds.map((f) => (f.x1 === f.x2 ? f.x1 : f.y1).toFixed(1)).join(', ')} mm from the {t.folds[0]?.x1 === t.folds[0]?.x2 ? 'left' : 'top'} (guides only, never printed{classic && p.cropMarks ? ' — fold marks in the margin' : ''}).</li>
        </ul>
      </div>
    </div>
  );
}

/** Tear-off pad: the leaves are the page size; the carrier card is separate artwork with a keep-clear pad zone. */
function CarrierSettings({ doc, change }: { doc: ProjectDoc; change: Change }) {
  const p = doc.product;
  const c = p.carrier;
  const set = (patch: Partial<typeof c>) => change({ carrier: { ...c, ...patch } }, 'carrier');
  const pad = (patch: Partial<typeof c.pad>) => set({ pad: { ...c.pad, ...patch } });
  return (
    <div className="col">
      <Check label="Tear-off pad on a carrier card (e.g. magnet)" checked={c.enabled} disabled={p.duplex} onChange={(v) => change({ carrier: { ...c, enabled: v }, ...(v ? { includeCover: false } : {}) }, 'carrier')} />
      {c.enabled && (
        <div className="col" style={{ paddingLeft: 24 }}>
          <div className="grid2">
            <NumberField label="Carrier width" unit="mm" value={c.widthMm} min={30} max={1000} onChange={(v) => set({ widthMm: v })} />
            <NumberField label="Carrier height" unit="mm" value={c.heightMm} min={30} max={1000} onChange={(v) => set({ heightMm: v })} />
          </div>
          <div className="grid2">
            <NumberField label="Pad left" unit="mm" value={c.pad.x} min={0} max={1000} onChange={(v) => pad({ x: v })} />
            <NumberField label="Pad top" unit="mm" value={c.pad.y} min={0} max={1000} onChange={(v) => pad({ y: v })} />
            <NumberField label="Pad width" unit="mm" value={c.pad.w} min={0} max={1000} onChange={(v) => pad({ w: v })} />
            <NumberField label="Pad height" unit="mm" value={c.pad.h} min={0} max={1000} onChange={(v) => pad({ h: v })} />
          </div>
          <button className="btn sm" onClick={() => pad({ w: p.widthMm, h: p.heightMm, x: +((c.widthMm - p.widthMm) / 2).toFixed(1), y: +Math.max(0, c.heightMm - p.heightMm - (c.widthMm - p.widthMm) / 2).toFixed(1) })}>
            Fit the pad zone to the leaf size, centred at the bottom
          </button>
          <p className="small faint" style={{ margin: 0 }}>The carrier is exported with “Carrier card PDF” and after the leaves in the imposed file. Keep artwork out of the pad zone (it is shown on the carrier page).</p>
        </div>
      )}
    </div>
  );
}
